import { useRef, useState } from 'react'
import icons from '../../config/icons.config'
import Message from '../../model/Message'
import {
  createImagePreview,
  getImageValidationError,
  type ImageData,
  isValidImageFile,
  revokeImagePreview,
} from '../../util/ImageUtil'
import AppIcon from '../icons/AppIcon'
import styles from './ChatScreen.module.css'

interface ChatMessageInputProps {
  onSend: (
    messageText: string,
    replyToId?: string,
    images?: ImageData[],
    files?: File[],
  ) => Promise<void>
  replyTo?: Message
  onCancelReply?: () => void
  disabled?: boolean
}

export default function ChatMessageInput({
  onSend,
  replyTo,
  onCancelReply,
  disabled = false,
}: ChatMessageInputProps) {
  const [messageText, setMessageText] = useState('')
  const [selectedImages, setSelectedImages] = useState<ImageData[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [error, setError] = useState<string>()
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function handleSend() {
    if (disabled) return
    if (messageText.trim() === '' && selectedImages.length === 0) return
    setError(undefined)
    try {
      await onSend(messageText, replyTo?.id, selectedImages, selectedFiles)
    } catch {
      setError('Could not send. Try again.')
      return
    }
    setMessageText('')
    setSelectedImages([])
    setSelectedFiles([])
    imagePreviews.forEach(revokeImagePreview)
    setImagePreviews([])
  }

  function handleImageSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || [])
    const validFiles = files.filter(isValidImageFile)

    if (validFiles.length === 0 && files.length > 0) {
      setError(getImageValidationError(files[0]!))
      return
    }

    setError(undefined)

    const newImages: ImageData[] = []
    const newPreviews: string[] = []

    validFiles.forEach((file) => {
      const preview = createImagePreview(file)
      newPreviews.push(preview)
      newImages.push({ url: preview, alt: file.name })
    })

    setSelectedImages((prev) => [...prev, ...newImages])
    setImagePreviews((prev) => [...prev, ...newPreviews])
    setSelectedFiles((prev) => [...prev, ...validFiles])

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleRemoveImage(index: number) {
    revokeImagePreview(imagePreviews[index]!)
    setSelectedImages((prev) => prev.filter((_, i) => i !== index))
    setImagePreviews((prev) => prev.filter((_, i) => i !== index))
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <div className={styles.composer}>
      {replyTo && (
        <div className={styles.replyBar}>
          <div className={styles.replyBarText}>
            Replying to {replyTo.user.firstName}:{' '}
            {replyTo.message.length > 40
              ? `${replyTo.message.substring(0, 40)}...`
              : replyTo.message}
          </div>
          {onCancelReply && (
            <button
              type="button"
              className={styles.replyCancel}
              onClick={onCancelReply}
              aria-label="Cancel reply"
            >
              <AppIcon icon={icons.close} size={16} />
            </button>
          )}
        </div>
      )}

      {selectedImages.length > 0 && (
        <div className={styles.previewRow}>
          {selectedImages.map((image, index) => (
            <div key={index} className={styles.preview}>
              <img src={image.url} alt={image.alt} />
              <button
                type="button"
                className={styles.previewRemove}
                onClick={() => handleRemoveImage(index)}
                aria-label="Remove image"
              >
                <AppIcon icon={icons.close} size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p id="chat-send-error" className={styles.sendError} role="alert">
          {error}
        </p>
      )}

      <div className={styles.inputRow}>
        <input
          className={styles.input}
          value={messageText}
          onChange={(e) => {
            setMessageText(e.target.value)
            setError(undefined)
          }}
          placeholder="Type a message..."
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'chat-send-error' : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSend()
          }}
        />
        <input
          ref={fileInputRef}
          className={styles.hiddenInput}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageSelect}
        />
        <button
          type="button"
          className={[styles.iconBtn, styles.attachBtn].join(' ')}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Attach image"
        >
          <AppIcon icon={icons.image} size={20} />
        </button>
        <button
          type="button"
          className={[styles.iconBtn, styles.sendBtn].join(' ')}
          onClick={() => void handleSend()}
          disabled={disabled}
          aria-label="Send"
        >
          <AppIcon icon={icons.up} size={20} />
        </button>
      </div>
    </div>
  )
}
